import { createContext, useContext, type ReactNode } from 'react'

/**
 * Where the application is mounted, and what the application around it
 * provides.
 *
 * The screens navigate with paths of their own — `/regions/<id>`, `/checks` —
 * written as if the application owned the site. Mounted inside a host's
 * router at `/compliance`, those have to become `/compliance/regions/<id>`;
 * with a router of its own (`basename`), the router does that already. So
 * the prefix is applied only when the host's router is the one in charge.
 */
export interface Host {
  /** Where the application lives under the host's routes, e.g. `/compliance`. `''` for the root. */
  basePath: string
  /** Whose router: the host's (paths need the prefix), or one of its own (they do not). */
  router: 'host' | 'own'
  /** Shown where the API says there is no signed-in caller. */
  signedOut?: ReactNode
  /** Offered as "Sign out" in the account menu; no entry without it. */
  onSignOut?: () => void
  /**
   * The host's own home, such as its dashboard. The mark and the first crumb
   * go there when given; without it they go to the application's home.
   */
  onHome?: () => void
  /** Names one of the host's users, by the API's user id; see `PolygonChecksProps.userLabel`. */
  userLabel?: (userId: string) => string | undefined | Promise<string | undefined>
  /** The host's own references to capture; see `PolygonChecksProps.references`. */
  references?: Reference[]
}

/** One of the host's options for a reference: its id, and how to show it. */
export interface ReferenceOption {
  value: string
  label: string
}

/**
 * A reference the host wants on uploads and plots, e.g. a contract. The key
 * names it in the API and exports (`ref_<key>`): lower case letters, digits
 * and _. With `search`, the options are the host's; without, it is typed.
 */
export interface Reference {
  key: string
  label: string
  /** The upload cannot be validated until it has one. */
  required?: boolean
  search?: (query: string) => ReferenceOption[] | Promise<ReferenceOption[]>
}

const HostContext = createContext<Host>({ basePath: '', router: 'own' })

export const HostProvider = HostContext.Provider

export const useHost = () => useContext(HostContext)

/** `/compliance/` → `/compliance`; `` and `/` → ``. */
export const cleanBasePath = (path: string | undefined) => {
  const trimmed = (path ?? '').trim().replace(/\/+$/, '')
  return trimmed && !trimmed.startsWith('/') ? `/${trimmed}` : trimmed
}
